import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { ImpactRating } from "../../../__generated__/globalTypes";
import useUserInfo from "../../../data-store/useUserInfo";
import { impactCategoryToEffectTypePerKg } from "../../../domain/EffectType";
import { ImpactCategory } from "../../../domain/impactCategories";
import { convertUnitsForDisplay } from "../../../util/units";
import { recipeOrderByImpactIntensity } from "../../dashboard/HighestAndLowestImpactProducts";
import Label from "../../labels/Label";
import { usePages } from "../../pages";
import { LinkButton } from "../../utils/Button";
import { ArrowRight } from "../../utils/Vectors";
import "./ProductHighlights.css";

interface ProductHighlightInfo {
  impactIntensity: number;
  productId: number;
  productName: string;
}

interface ProductHighlightsProps {
  highImpactProduct: ProductHighlightInfo | null;
  impactCategory: ImpactCategory;
  lowImpactProduct: ProductHighlightInfo | null;
}

export function ProductHighlights(props: ProductHighlightsProps) {
  const { highImpactProduct, lowImpactProduct, impactCategory } = props;

  const [{ locale }] = useUserInfo();

  const label = (impactRating: ImpactRating) => (
    <Label
      colourSetting="colour"
      impactCategory={impactCategory}
      impactRating={impactRating}
      type="letterRating"
      width={46}
      locale={locale}
    />
  );

  return (
    <div className="ProductHighlights">
      <h4 className="m-0">
        <FormattedMessage
          id="components/sales-dashboard/ProductHighlights:title"
          defaultMessage="Product Highlights"
        />
      </h4>
      <ProductHighlight
        icon={label(ImpactRating.VHIGH)}
        impactCategory={impactCategory}
        noDataMessage={
          <FormattedMessage
            id="components/sales-dashboard/ProductHighlights:noERatedData"
            defaultMessage="No E-rated products in this sales period"
          />
        }
        product={highImpactProduct}
        title={
          <FormattedMessage
            id="components/sales-dashboard/ProductHighlights:highImpactProduct"
            defaultMessage="Best-Selling E-Rated Product"
          />
        }
        viewMoreLinkQueryParams={{
          ...recipeImpactRatingFilter(impactCategory, ImpactRating.VHIGH),
          impactCategory,
          orderBy: recipeOrderByImpactIntensity(impactCategory, false),
          view: "list",
        }}
      />
      <ProductHighlight
        icon={label(ImpactRating.VLOW)}
        impactCategory={impactCategory}
        noDataMessage={
          <FormattedMessage
            id="components/sales-dashboard/ProductHighlights:noARatedData"
            defaultMessage="No A-rated products in this sales period"
          />
        }
        product={lowImpactProduct}
        title={
          <FormattedMessage
            id="components/sales-dashboard/ProductHighlights:lowImpactProduct"
            defaultMessage="Best-Selling A-Rated Product"
          />
        }
        viewMoreLinkQueryParams={{
          ...recipeImpactRatingFilter(impactCategory, ImpactRating.VLOW),
          impactCategory,
          orderBy: recipeOrderByImpactIntensity(impactCategory, true),
          view: "list",
        }}
      />
    </div>
  );
}

interface ProductHighlightProps {
  icon: React.ReactNode;
  impactCategory: ImpactCategory;
  noDataMessage: React.ReactNode;
  product: ProductHighlightInfo | null;
  title: React.ReactNode;
  viewMoreLinkQueryParams: Record<string, string>;
}

function ProductHighlight(props: ProductHighlightProps) {
  const {
    icon,
    impactCategory,
    noDataMessage,
    product,
    title,
    viewMoreLinkQueryParams,
  } = props;

  const history = useHistory();
  const intl = useIntl();
  const pages = usePages();
  const effectTypePerKg = impactCategoryToEffectTypePerKg(impactCategory);
  const recipePageURLParams = new URLSearchParams(viewMoreLinkQueryParams);

  const impactIntensityInAppropriateUnits =
    product === null
      ? null
      : convertUnitsForDisplay(
          intl,
          product.impactIntensity,
          effectTypePerKg.internalBaseUnits.unitToConvert,
          effectTypePerKg.internalBaseUnits.otherBaseUnits,
          "kg",
          2,
          2
        );

  return (
    <div className="ProductHighlight">
      <div className="ProductHighlight__TitleAndLink">
        <div className="body-copy-medium">{title}</div>
        {product !== null && (
          <LinkButton
            className="p-0"
            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
              e.preventDefault();
              history.push({
                pathname: pages.Recipes.url,
                search: recipePageURLParams.toString(),
              });
            }}
          >
            <div className="ProductHighlight__TitleAndLink__Link small-copy-medium">
              <FormattedMessage
                id="components/sales-dashboard/ProductHighlights:viewMore"
                defaultMessage="View more"
              />
              <ArrowRight width={16} />
            </div>
          </LinkButton>
        )}
      </div>
      {product ? (
        <LinkButton
          className="ProductHighlight__LinkButton p-0"
          onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
            e.preventDefault();
            history.push(pages.Recipe.url({ id: product.productId }));
          }}
        >
          <div className="ProductHighlight__Card">
            <div className="ProductHighlight__Card__Link body-copy-medium">
              {product.productName}
            </div>
            <div className="ProductHighlight__NumberAndRatingLabel">
              <div className="ProductHighlight__NumberAndUnit">
                <div className="Number">
                  {impactIntensityInAppropriateUnits?.convertedValue}
                </div>
                <div className="small-copy">
                  {impactIntensityInAppropriateUnits?.unitString}
                </div>
              </div>
              {icon}
            </div>
          </div>
        </LinkButton>
      ) : (
        <div className="ProductHighlight__Card__NoData">
          <div className="ProductHighlight__Card__NoData__Text">
            {noDataMessage}
          </div>
          <div className="ProductHighlight__NumberAndRatingLabel__NoData">
            {icon}
          </div>
        </div>
      )}
    </div>
  );
}

function recipeImpactRatingFilter(
  impactCategory: ImpactCategory,
  impactRating: ImpactRating
): Record<string, ImpactRating> {
  switch (impactCategory) {
    case ImpactCategory.GHG:
      return {
        filterToSelectedGhgImpactRatings: impactRating,
      };
    case ImpactCategory.LAND_USE:
      return {
        filterToSelectedLandUseImpactRatings: impactRating,
      };
    case ImpactCategory.WATER_USE:
      return {
        filterToSelectedWaterUseImpactRatings: impactRating,
      };
  }
}
